import { locations } from '@/api/locations';
import { materialEntities } from '@/api/materialEntities';
import { memos } from '@/api/memos';
import { coordinatesService } from '@/services/map/coordinatesService';
import type { MapAsset, ViewBounds } from '@/types/map';
import L from 'leaflet';
import 'leaflet.markercluster';
import { computed, ref } from 'vue';

// État global partagé
const map = ref<L.Map | null>(null);
const markerClusterGroup = ref<L.MarkerClusterGroup | null>(null);
const markers = ref<Map<string, L.Marker>>(new Map());
const currentViewBounds = ref<ViewBounds | null>(null);
const assets = ref<MapAsset[]>([]);

// Timer pour debouncer setMapAssets
let setMapAssetsTimer: ReturnType<typeof setTimeout> | null = null;

// Configuration de la carte
const MAP_CONFIG = {
  center: [46.603354, 1.888334] as [number, number],
  zoom: 6,
  minZoom: 2,
  maxZoom: 18
};

// Configuration des markers
const MARKER_COLORS = {
  location: '#3b82f6',
  materialEntity: '#10b981', 
  memo: '#f59e0b'
};

export const useMapLogic = () => {
  // Initialiser la carte
  const initializeMap = async (container: HTMLElement): Promise<void> => {
    try {
      // Créer la carte
      map.value = L.map(container, {
        center: MAP_CONFIG.center,
        zoom: MAP_CONFIG.zoom,
        minZoom: MAP_CONFIG.minZoom,
        maxZoom: MAP_CONFIG.maxZoom,
        zoomControl: true,
        attributionControl: true
      });

      // Ajouter les tuiles
      const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: MAP_CONFIG.maxZoom
      });
      tileLayer.addTo(map.value as any);

      // Créer le groupe de clustering avec configuration améliorée
      markerClusterGroup.value = L.markerClusterGroup({
        maxClusterRadius: 60,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        spiderfyOnMaxZoom: true,
        removeOutsideVisibleBounds: false,
        animate: false, // Désactive l'animation des markers — évite _latLngToNewLayerPoint null
        iconCreateFunction: (cluster: any) => {
          const count = cluster.getChildCount();
          let className = 'marker-cluster-small';
          
          if (count < 10) {
            className = 'marker-cluster-small';
          } else if (count < 100) {
            className = 'marker-cluster-medium';
          } else {
            className = 'marker-cluster-large';
          }

          return L.divIcon({
            html: `<div><span>${count}</span></div>`,
            className: `marker-cluster ${className}`,
            iconSize: L.point(40, 40),
            iconAnchor: [20, 20]
          });
        }
      });

      // CORRECTION DU BUG: Gérer le clic cluster manuellement avec protection
      markerClusterGroup.value.on('clusterclick', (event: L.LeafletEvent & { layer: any }) => {
        try {
          const cluster = event.layer;
          const bounds = cluster.getBounds();
          
          // Vérifier si la carte est prête et si on peut encore zoomer
          if (map.value && map.value.getZoom() < MAP_CONFIG.maxZoom - 2) {
            // Arrêter toute animation en cours
            map.value.stop();
            
            // Zoomer vers le cluster avec animation fluide
            setTimeout(() => {
              if (map.value) {
                map.value.flyToBounds(bounds, {
                  duration: 0.8,
                  easeLinearity: 0.25,
                  maxZoom: Math.min(map.value.getZoom() + 3, MAP_CONFIG.maxZoom - 1)
                });
              }
            }, 50);
          } else if (cluster.spiderfy) {
            // Si on ne peut plus zoomer, faire un spiderfy
            cluster.spiderfy();
          }
        } catch (error) {
          console.warn('⚠️ Erreur lors du clic cluster:', error);
        }
      });

      map.value.addLayer(markerClusterGroup.value as any);

      // Écouter les changements de vue avec debouncing
      let boundsTimeout: number;
      const updateBounds = () => {
        clearTimeout(boundsTimeout);
        boundsTimeout = window.setTimeout(() => {
          if (map.value) {
            const bounds = map.value.getBounds();
            currentViewBounds.value = {
              north: bounds.getNorth(),
              south: bounds.getSouth(),
              east: bounds.getEast(),
              west: bounds.getWest()
            };
          }
        }, 300);
      };

      map.value.on('moveend', updateBounds);
      map.value.on('zoomend', updateBounds);

      // Charger les assets
      await loadAssets();
      
      console.log('✅ Carte initialisée avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation de la carte:', error);
    }
  };

  // Charger les assets depuis les APIs
  const loadAssets = async (): Promise<void> => {
    try {
      const [locationsData, materialEntitiesData, memosData] = await Promise.all([
        locations.getAccessible(),
        materialEntities.getAccessible(), 
        memos.getAccessible()
      ]);

      const allAssets: MapAsset[] = [
        ...coordinatesService.extractLocationAssets(locationsData),
        ...coordinatesService.extractMaterialEntityAssets(materialEntitiesData),
        ...coordinatesService.extractMemoAssets(memosData)
      ];

      assets.value = allAssets;
      console.log('📍 Assets chargés:', allAssets.length);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des assets:', error);
    }
  };

  // Créer un marker personnalisé
  const createMarker = (asset: MapAsset): L.Marker => {
    const color = MARKER_COLORS[asset.type];
    
    // Icône personnalisée PLUS GRANDE
    const icon = L.divIcon({
      html: `
        <div style="
          background-color: ${color};
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 3px 6px rgba(0,0,0,0.4);
          position: relative;
        ">
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            background-color: white;
            border-radius: 50%;
          "></div>
        </div>
      `,
      className: 'custom-marker',
      iconSize: [26, 26],
      iconAnchor: [13, 13]
    });

    const marker = L.marker([asset.coordinates.latitude, asset.coordinates.longitude], { icon });

    // Tooltip au hover
    marker.bindTooltip(asset.title, {
      permanent: false,
      direction: 'top',
      offset: [0, -15],
      className: 'custom-tooltip'
    });

    // Popup au clic avec z-index élevé
    marker.bindPopup(`
      <div class="p-3">
        <h3 class="font-medium text-gray-900 mb-2">${asset.title}</h3>
        <p class="text-sm text-gray-600 mb-3">${asset.coordinates.latitude.toFixed(4)}, ${asset.coordinates.longitude.toFixed(4)}</p>
        <button onclick="window.location.href='/${asset.type}/${asset.uuid}'" 
                class="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
          Voir détails
        </button>
      </div>
    `, {
      closeButton: true,
      autoClose: true,
      closeOnEscapeKey: true,
      maxWidth: 250,
      className: 'custom-popup'
    });

    return marker;
  };

  // Applique réellement le changement de markers (hors animation)
  const applyMapAssets = (newAssets: MapAsset[]): void => {
    if (!markerClusterGroup.value || !map.value) return;
    try {
      map.value.stop();
      markerClusterGroup.value.clearLayers();
      markers.value.clear();
      newAssets.forEach(asset => {
        try {
          const marker = createMarker(asset);
          if (markerClusterGroup.value && map.value) {
            markerClusterGroup.value.addLayer(marker);
            markers.value.set(asset.uuid, marker);
          }
        } catch (error) {
          console.warn('⚠️ Erreur création marker:', asset.uuid, error);
        }
      });
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour des markers:', error);
    }
  };

  // Mettre à jour les markers — debounce + attente fin d'animation zoom
  const setMapAssets = (newAssets: MapAsset[]): void => {
    if (!markerClusterGroup.value || !map.value) return;

    // Annuler l'éventuel timer précédent
    if (setMapAssetsTimer !== null) {
      clearTimeout(setMapAssetsTimer);
      setMapAssetsTimer = null;
    }

    setMapAssetsTimer = setTimeout(() => {
      setMapAssetsTimer = null;
      if (!markerClusterGroup.value || !map.value) return;

      // Si un zoom est en cours, attendre la fin avant de modifier les layers
      if ((map.value as any)._animatingZoom) {
        map.value.once('zoomend', () => applyMapAssets(newAssets));
      } else {
        applyMapAssets(newAssets);
      }
    }, 150);
  };

  // Centrer sur un asset
  const centerOnAsset = (asset: MapAsset): void => {
    if (!map.value) return;

    try {
      map.value.flyTo([asset.coordinates.latitude, asset.coordinates.longitude], 15, {
        duration: 1.0,
        easeLinearity: 0.25
      });
      
      // Ouvrir le popup du marker correspondant
      const marker = markers.value.get(asset.uuid);
      if (marker) {
        setTimeout(() => marker.openPopup(), 1000);
      }
    } catch (error) {
      console.error('❌ Erreur lors du centrage:', error);
    }
  };

  // Réinitialiser la vue
  const resetMapView = (): void => {
    if (!map.value || !markerClusterGroup.value || markers.value.size === 0) return;

    try {
      const group = L.featureGroup(Array.from(markers.value.values()) as any);
      if (group.getBounds().isValid()) {
        map.value.flyToBounds(group.getBounds(), {
          padding: [20, 20],
          duration: 1.0,
          easeLinearity: 0.25
        });
      }
      
      // Réinitialiser les bounds après un délai
      setTimeout(() => {
        currentViewBounds.value = null;
      }, 1500);
    } catch (error) {
      console.error('❌ Erreur lors de la réinitialisation:', error);
    }
  };

  const destroyMap = (): void => {
    if (setMapAssetsTimer !== null) {
      clearTimeout(setMapAssetsTimer);
      setMapAssetsTimer = null;
    }
    if (map.value) {
      map.value.remove();
      map.value = null;
    }
    markerClusterGroup.value = null;
    markers.value.clear();
    currentViewBounds.value = null;
  };

  return {
    assets: computed(() => assets.value),
    currentViewBounds: computed(() => currentViewBounds.value),
    initializeMap,
    centerOnAsset,
    resetMapView,
    setMapAssets,
    destroyMap
  };
};